import React, { useState, useEffect } from 'react';
import "./Navbar.css";
import { FaBarsStaggered, FaChevronDown } from "react-icons/fa6";
import Mobileview from './Mobileview/Mobileview';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { animateScroll as scroll } from "react-scroll";
import { 
  BsCpu, 
  BsBriefcase, 
  BsRobot, 
  BsCodeSquare, 
  BsGlobe, 
  BsGraphUpArrow, 
  BsPalette, 
  BsLayers, 
  BsChatSquareDots, 
  BsPeople, 
  BsNewspaper, 
  BsShieldCheck,
  BsArrowRight,
  BsDownload,
  BsPhone,
  BsLaptop,
  BsApple,
  BsAndroid2,
  BsShare,
  BsPlusSquare,
  BsCheckCircleFill,
  BsStars
} from "react-icons/bs";

const Navbar = () => {
  const [show, setShow] = useState(false);
  const [shadow, setShadow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(() => {
    return sessionStorage.getItem('pwaBannerDismissed') !== 'true';
  });
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isDarkHeader = isHome || location.pathname === '/portfolio' || location.pathname === '/contactus';

  const handleLinkClick = () => {
    setActiveDropdown(null);
    scroll.scrollToTop();
  };

  useEffect(() => {
    // Check if running as installed standalone PWA
    const inStandalone = 
      ('standalone' in window.navigator && window.navigator.standalone) ||
      window.matchMedia('(display-mode: standalone)').matches;
    if (inStandalone) {
      setIsStandalone(true);
    }

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Keep banner visible
      setShowInstallBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', () => {
      setIsStandalone(true);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
    });

    const handleScroll = () => {
      if (window.scrollY > 30) {
        setShadow(true);
      } else {
        setShadow(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setShowInstallBanner(false);
        }
        setDeferredPrompt(null);
      } catch (err) {
        setShowInstallModal(true);
      }
    } else {
      setShowInstallModal(true);
    }
  };

  const handleDismissBanner = () => {
    sessionStorage.setItem('pwaBannerDismissed', 'true');
    setShowInstallBanner(false);
  };

  return (
    <>
      {/* ===== BEAUTIFUL TOP BAR: INSTALL REACT APP ===== */}
      {showInstallBanner && !isStandalone && (
        <aside aria-label="Install App" className="relative bg-gradient-to-r from-[#070a12] via-[#101726] to-[#070a12] text-white border-b border-[#f06600]/30 shadow-lg shadow-black/20 z-[1000] text-xs font-sans overflow-hidden">
          {/* Subtle Ambient Orange Glow Beam */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#f06600]/10 to-transparent pointer-events-none" />
          <div className="absolute top-0 inset-x-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#f06600] to-transparent opacity-80" />

          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-2.5 flex items-center justify-between gap-3 relative">
            {/* Left & Center: App Branding & Selling Points */}
            <div className="flex items-center gap-2.5 sm:gap-3.5 flex-1 min-w-0">
              {/* Glowing App Avatar */}
              <div className="relative shrink-0 flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-slate-900 border border-[#f06600]/50 shadow-md shadow-[#f06600]/25 p-1">
                <img 
                  src="/logo.png" 
                  alt="Beta Communes" 
                  className="w-full h-full object-contain" 
                />
                <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#f06600] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#f06600]" />
                </span>
              </div>

              {/* Message & Benefits */}
              <div className="flex items-center gap-2 flex-wrap min-w-0 text-xs sm:text-sm">
                <span className="inline-flex items-center gap-1 bg-[#f06600]/20 text-[#f06600] border border-[#f06600]/40 text-[10px] sm:text-[11px] font-black uppercase px-2 py-0.5 rounded-full tracking-wider shrink-0">
                  <BsStars className="text-[10px]" />
                  APP
                </span>

                <span className="font-extrabold text-white truncate">
                  Install Beta Communes App
                </span>

                <span className="text-slate-400 text-xs hidden lg:inline">
                  — 1-click access, offline support & lightning-fast performance!
                </span>
                <span className="text-slate-400 text-[11px] hidden sm:inline lg:hidden">
                  — Fast, native & offline access
                </span>
              </div>
            </div>

            {/* Right: CTA & Dismiss Buttons */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleInstallClick}
                className="group relative bg-gradient-to-r from-[#f06600] via-[#ff7300] to-amber-500 hover:from-[#d05800] hover:to-amber-600 text-white font-bold px-3.5 sm:px-4 py-1.5 rounded-full text-xs shadow-md shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                title="Install Beta Communes App on your device"
              >
                <BsDownload className="text-xs transition-transform group-hover:-translate-y-0.5" />
                <span>Install Now</span>
              </button>

              <button
                onClick={handleDismissBanner}
                className="text-slate-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10 text-xs"
                aria-label="Dismiss banner"
                title="Close top bar"
              >
                ✕
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* ===== INSTALLATION GUIDE MODAL (for iOS, Safari, Firefox & Desktop) ===== */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1100] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full border border-gray-200 overflow-hidden text-gray-900 animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#080c14] via-[#131c2d] to-[#080c14] text-white p-6 relative">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-slate-900 border border-[#f06600]/50 p-1.5 flex items-center justify-center shadow-lg shadow-[#f06600]/20">
                  <img src="/logo.png" alt="Beta Communes" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Install Beta Communes</h3>
                  <p className="text-xs text-slate-300">Add the app directly to your home screen or desktop</p>
                </div>
              </div>
              <button
                onClick={() => setShowInstallModal(false)}
                className="absolute top-5 right-5 text-slate-400 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition-colors"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            {/* Modal Instructions */}
            <div className="p-6 space-y-4">
              {/* Desktop Instruction */}
              <div className="flex items-start gap-3.5 p-3.5 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl shrink-0 mt-0.5">
                  <BsLaptop className="w-5 h-5" />
                </div>
                <div className="text-xs space-y-1">
                  <strong className="text-sm font-bold text-gray-900 block">Chrome & Edge (Desktop)</strong>
                  <p className="text-gray-600 leading-relaxed">
                    Look for the <strong>Install icon (⊕)</strong> on the right side of your browser's address bar and click <strong>"Install"</strong>, or click menu <strong>(⋮) &rarr; "Install Beta Communes"</strong>.
                  </p>
                </div>
              </div>

              {/* iOS Safari Instruction */}
              <div className="flex items-start gap-3.5 p-3.5 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="p-2.5 bg-orange-50 text-[#f06600] rounded-xl shrink-0 mt-0.5">
                  <BsApple className="w-5 h-5" />
                </div>
                <div className="text-xs space-y-1">
                  <strong className="text-sm font-bold text-gray-900 block">Apple iOS (iPhone & iPad)</strong>
                  <p className="text-gray-600 leading-relaxed">
                    Tap the <strong>Share button (📤)</strong> at the bottom of Safari, scroll down, and select <strong>"Add to Home Screen" (➕)</strong>.
                  </p>
                </div>
              </div>

              {/* Android Instruction */}
              <div className="flex items-start gap-3.5 p-3.5 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl shrink-0 mt-0.5">
                  <BsAndroid2 className="w-5 h-5" />
                </div>
                <div className="text-xs space-y-1">
                  <strong className="text-sm font-bold text-gray-900 block">Android (Chrome & Samsung)</strong>
                  <p className="text-gray-600 leading-relaxed">
                    Tap the <strong>Menu (⋮)</strong> in the top right corner of Chrome and select <strong>"Install App"</strong> or <strong>"Add to Home screen"</strong>.
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-2">
                <button
                  onClick={() => setShowInstallModal(false)}
                  className="w-full py-3 bg-[#f06600] hover:bg-[#d05800] text-white font-bold rounded-2xl transition-all text-sm shadow-md shadow-orange-500/25"
                >
                  Got It!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        <header className={`sticky top-0 z-[990] transition-all duration-300 ${
          isDarkHeader 
            ? (shadow ? 'bg-[#080c14]/95 backdrop-blur-md border-b border-slate-800/80 py-3 shadow-xl shadow-black/20' : 'bg-[#080c14] border-b border-slate-800/40 py-3.5')
            : (shadow ? 'bg-white/95 backdrop-blur-md border-b border-slate-200 py-3 shadow-md shadow-black/5' : 'bg-white border-b border-slate-200/80 py-3.5')
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          
          {/* Logo Section */}
          <div className="flex items-center">
            <Link to="/" onClick={handleLinkClick} className="flex items-center gap-2.5 group">
              <img 
                src="/logo.png" 
                alt="Beta Communes" 
                className="h-9 sm:h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105" 
              />
              <div className="flex flex-col">
                <span className={`text-base font-extrabold tracking-tight leading-none ${isDarkHeader ? 'text-white' : 'text-slate-900'}`}>
                  Beta Communes
                </span>
                <span className={`text-[10px] font-medium tracking-wide mt-0.5 ${isDarkHeader ? 'text-slate-400' : 'text-slate-500'}`}>
                  Build · Grow · Together
                </span>
              </div>
            </Link>
          </div>

          {/* Minimal Agency Navigation */}
          <nav className={`hidden lg:flex items-center gap-7 text-[13.5px] font-medium ${isDarkHeader ? 'text-slate-300' : 'text-slate-600'}`}>
            
            <NavLink 
              to="/" 
              onClick={handleLinkClick} 
              className={({ isActive }) => `transition-colors py-1 ${isActive ? 'text-[#f06600] font-semibold' : 'hover:text-[#f06600]'}`}
            >
              Home
            </NavLink>

            <NavLink 
              to="/aboutus" 
              onClick={handleLinkClick} 
              className={({ isActive }) => `transition-colors py-1 ${isActive ? 'text-[#f06600] font-semibold' : 'hover:text-[#f06600]'}`}
            >
              About
            </NavLink>

            <NavLink 
              to="/services" 
              onClick={handleLinkClick} 
              className={({ isActive }) => `transition-colors py-1 ${isActive ? 'text-[#f06600] font-semibold' : 'hover:text-[#f06600]'}`}
            >
              Services
            </NavLink>

            <NavLink 
              to="/portfolio" 
              onClick={handleLinkClick} 
              className={({ isActive }) => `transition-all px-3 py-1 rounded-full ${
                isActive 
                  ? (isDarkHeader ? 'bg-[#f06600]/20 text-[#f06600] font-bold border border-[#f06600]/40' : 'bg-orange-50 text-[#f06600] font-bold border border-orange-200')
                  : 'hover:text-[#f06600]'
              }`}
            >
              Work
            </NavLink>

            <NavLink 
              to="/blog" 
              onClick={handleLinkClick} 
              className={({ isActive }) => `transition-colors py-1 ${isActive ? 'text-[#f06600] font-semibold' : 'hover:text-[#f06600]'}`}
            >
              Insights
            </NavLink>

            <NavLink 
              to="/contactus" 
              onClick={handleLinkClick} 
              className={({ isActive }) => `transition-all px-3 py-1 rounded-full ${
                isActive 
                  ? (isDarkHeader ? 'bg-[#f06600]/20 text-[#f06600] font-bold border border-[#f06600]/40' : 'bg-orange-50 text-[#f06600] font-bold border border-orange-200')
                  : 'hover:text-[#f06600]'
              }`}
            >
              Contact
            </NavLink>

            {/* Portal Link */}
            <NavLink 
              to="/portal/login" 
              onClick={handleLinkClick}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${
                isDarkHeader 
                  ? 'border-slate-800 bg-slate-900/80 text-slate-300 hover:text-white hover:border-[#f06600]' 
                  : 'border-slate-200 bg-slate-50 text-slate-600 hover:text-[#f06600] hover:border-[#f06600]'
              }`}
            >
              <BsShieldCheck className="text-xs text-[#f06600]" />
              <span>Portal</span>
            </NavLink>

          </nav>

          {/* Right Action Button: Let's Talk */}
          <div className="hidden lg:flex items-center">
            <Link to="/contactus" onClick={handleLinkClick}>
              <button className="bg-[#f06600] hover:bg-[#d05800] text-white font-semibold px-5 py-2 rounded-full shadow-md shadow-orange-500/25 hover:shadow-orange-500/35 transition-all duration-300 transform hover:-translate-y-0.5 flex items-center gap-1.5 text-xs tracking-wide cursor-pointer">
                <span>Let's Talk</span>
                <BsArrowRight className="text-sm" />
              </button>
            </Link>
          </div>

          {/* Mobile Menu Icon */}
          <div className="lg:hidden flex items-center gap-3">
            <Link to="/portal/login" onClick={handleLinkClick} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-[#f06600] text-[#f06600] bg-orange-500/10">
              Portal
            </Link>
            <button 
              onClick={() => setShow(true)}
              className={`p-2 focus:outline-none ${isDarkHeader ? 'text-white hover:text-[#f06600]' : 'text-slate-800 hover:text-[#f06600]'}`}
              aria-label="Open navigation menu"
            >
              <FaBarsStaggered className="text-2xl" />
            </button>
          </div>

        </div>
      </header>

      {/* Mobile Menu Modal */}
      {show && <Mobileview setshow={setShow} />}
    </>
  );
};

export default Navbar;

